import React, {
  Context,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { merge } from 'lodash';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { User } from 'src/utils/safelyExtractUserRoleId';
import { getBrandingOverrides } from './getBrandingOverrides';
import { defaults } from './defaultBranding';
import { applyUserOverrides } from './applyUserBranding';
import { IBrandingOverrides, ITenantID, Role } from './types';

export interface IBrandingContextValue {
  branding: IBrandingOverrides;
  isBranded: boolean;
}

interface IBrandingProviderProps {
  children: ReactNode;
  tenantId: ITenantID | null;
}

export const BrandingContext = createContext<IBrandingContextValue>({
  branding: { ...defaults },
  isBranded: false,
});

export const BrandingProvider = ({
  children,
  tenantId,
}: IBrandingProviderProps) => {
  const [branding, setBranding] = useState<IBrandingOverrides>({
    ...defaults,
  });

  const [isBranded, setIsBranded] = useState<boolean>(false);

  const user: User | undefined = useSelector(selectAuthenticatedUser);

  useEffect(() => {
    async function loadBranding() {
      if (!tenantId) {
        return;
      }

      const brand = await getBrandingOverrides(tenantId);
      if (brand.length === 0) {
        return;
      }

      const role = user?.roles?.[0]?.role?.name as Role | undefined;
      if (!role) {
        return;
      }

      const updatedBrand = applyUserOverrides(brand[0], role);

      setBranding(merge(branding, { ...updatedBrand }));
      setIsBranded(true);
    }

    loadBranding();
  }, [tenantId, user]);

  return (
    <BrandingContext.Provider value={{ branding, isBranded }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = (
  context: Context<IBrandingContextValue> = BrandingContext
): IBrandingContextValue => {
  const brandingContext = useContext(context);

  if (brandingContext === undefined) {
    throw new Error('UseBranding must be used within Branding context');
  }

  return {
    branding: brandingContext.branding,
    isBranded: brandingContext.isBranded,
  };
};
